package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.document
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

private enum class DialogKind { Alert, Confirm, Prompt }

private class DialogRequest(
    val message: String,
    val kind: DialogKind,
    val initialValue: String = "",
    val onResult: (String?) -> Unit = {}
)

/** Shared queue: feedback survives closing the screen that produced it. */
object AppDialogs {
    private val requests = mutableStateListOf<DialogRequest>()

    fun alert(message: String) {
        requests.add(DialogRequest(message, DialogKind.Alert))
    }

    fun confirm(message: String, onConfirm: () -> Unit) {
        requests.add(DialogRequest(message, DialogKind.Confirm) { result ->
            if (result != null) onConfirm()
        })
    }

    fun prompt(message: String, initialValue: String = "", onResult: (String?) -> Unit) {
        requests.add(DialogRequest(message, DialogKind.Prompt, initialValue, onResult))
    }

    @Composable
    fun Host() {
        val request = requests.firstOrNull() ?: return
        key(request) {
            var value by remember { mutableStateOf(request.initialValue) }
            fun finish(result: String?) {
                if (requests.firstOrNull() !== request) return
                requests.removeAt(0)
                request.onResult(result)
            }

            DisposableEffect(request) {
                val previousFocus = document.activeElement as? HTMLElement
                onDispose { previousFocus?.focus() }
            }
            LaunchedEffect(request) {
                val dialog = document.getElementById("app-dialog")
                (dialog?.querySelector("input, button") as? HTMLElement)?.focus()
            }

            Div({
                style {
                    position(Position.Fixed)
                    top(0.px); right(0.px); bottom(0.px); left(0.px)
                    property("z-index", "2147483647")
                    backgroundColor(rgba(0, 0, 0, 0.45))
                }
                onKeyDown { event ->
                    event.stopPropagation()
                    when (event.key) {
                        "Escape" -> { event.preventDefault(); finish(null) }
                        "Enter" -> if (event.target is org.w3c.dom.HTMLInputElement) {
                            event.preventDefault()
                            finish(value)
                        }
                        "Tab" -> {
                            val controls = document.getElementById("app-dialog")?.querySelectorAll("input, button")
                            if (controls != null && controls.length > 0) {
                                val first = controls.item(0) as HTMLElement
                                val last = controls.item(controls.length - 1) as HTMLElement
                                if (event.shiftKey && document.activeElement == first) {
                                    event.preventDefault(); last.focus()
                                } else if (!event.shiftKey && document.activeElement == last) {
                                    event.preventDefault(); first.focus()
                                }
                            }
                        }
                    }
                }
            }) {
                PopupEngineCard(
                    windowKey = "app-dialog",
                    widthPx = 480,
                    heightPx = null,
                    minWidthPx = 280,
                    minHeightPx = 120,
                    centered = true,
                    viewportAnchored = true,
                    resizable = false,
                    showCloseIcon = false,
                    onClose = { finish(null) }
                ) {
                    Div({
                        id("app-dialog")
                        attr("role", if (request.kind == DialogKind.Alert) "alertdialog" else "dialog")
                        attr("aria-modal", "true")
                        attr("aria-labelledby", "app-dialog-message")
                        style {
                            padding(24.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(20.px)
                            color(Color.white)
                            property("max-height", "calc(100dvh - 80px)")
                            overflow("auto")
                            property("overflow-wrap", "anywhere")
                        }
                    }) {
                        Div({
                            id("app-dialog-message")
                            style { property("white-space", "pre-wrap"); textAlign("center") }
                        }) { Text(request.message) }
                        if (request.kind == DialogKind.Prompt) {
                            Input(InputType.Text) {
                                value(value)
                                attr("aria-labelledby", "app-dialog-message")
                                onInput { value = it.value }
                                style {
                                    padding(10.px)
                                    borderRadius(8.px)
                                    property("border", "1px solid rgba(255,255,255,0.3)")
                                    backgroundColor(rgba(255, 255, 255, 0.06))
                                    color(Color.white)
                                    minWidth(0.px)
                                }
                            }
                        }
                        Div({
                            style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); gap(10.px) }
                        }) {
                            if (request.kind != DialogKind.Alert) {
                                LumaticaButton("Abbrechen", onClick = { finish(null) })
                            }
                            LumaticaButton("OK", onClick = { finish(value) }, variant = LumaticaButtonVariant.Action)
                        }
                    }
                }
            }
        }
    }
}
